import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { estimateSdk as estimateSdkRaw, useEstimateSdk, useSaveSample } from '@vlabs/api/artemy/client';
import { DEFAULT_BBOX_STYLES } from '@vlabs/shared/components/photo-upload-form/BboxStyles';
import { FileDropzone } from '@vlabs/uikit/components/controls/file-dropzone/FileDropzone';
import type { TFileDropzoneFile } from '@vlabs/uikit/components/controls/file-dropzone/FileDropzone';
import type { WithClassNameProp } from '@vlabs/uikit/types';
import type { Argument } from 'classnames';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './FaceDetector.module.sass';
import { FileWithDetectionsViewer } from '../../../features/file-with-detections-viewer/FileWithDetectionsViewer';
import type { TBbox } from '../canvas';
import type { TBboxStyle } from '../canvas/bbox-renderer/types';

interface FaceDetectorDetection {
  sampleId: string;
  sdkDescriptor: string;
  rect: { x: number; y: number; width: number; height: number };
}

interface FaceDetectorPoint3Props extends WithClassNameProp {
  accept?: string[];
  maxImageSize?: number;
  disabledWhenDetections?: boolean;
  bboxStyles?: TBboxStyle;
  onChange?: (value: { sdkDescriptor: string; sampleId: string } | null | undefined) => void;
  dropzoneClassName?: Argument;
}

function base64ToBlob(base64: string): Blob {
  const byteCharacters = atob(base64);
  const byteArrays: Uint8Array[] = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }
  return new Blob(byteArrays, { type: 'image/jpeg' });
}

export function FaceDetectorPoint3({
  accept = undefined,
  bboxStyles = undefined,
  maxImageSize = undefined,
  disabledWhenDetections = true,
  onChange = undefined,
  className = undefined,
  dropzoneClassName = undefined,
}: FaceDetectorPoint3Props): React.JSX.Element {
  const { t } = useTranslation();
  const [file, setFile] = useState<TFileDropzoneFile>();
  const estimateSdk = useEstimateSdk({
    mutation: {
      mutationFn: async ({ data, params }) => {
        const blob = data as Blob;
        return estimateSdkRaw(blob, params, {
          headers: { 'Content-Type': blob.type },
        });
      },
    },
  });
  const saveSampleMutation = useSaveSample();
  const [error, setError] = useState<string>();
  const [detections, setDetections] = useState<FaceDetectorDetection[]>();
  const [selected, setSelected] = useState<FaceDetectorDetection>();
  const detectionsRef = useRef(detections);
  detectionsRef.current = detections;

  const resetDetections = () => {
    setDetections(undefined);
    setSelected(undefined);
  };
  const reset = () => {
    setFile(undefined);
    setError(undefined);
    resetDetections();
  };

  useEffect(() => {
    if (selected) {
      onChange?.({ sdkDescriptor: selected.sdkDescriptor, sampleId: selected.sampleId });
    } else {
      onChange?.(detections?.length ? null : undefined);
    }
  }, [selected, detections, onChange]);

  const onBBoxClick = useCallback((bbox: TBbox[] | null): void => {
    if (!bbox?.length) return;

    const id = bbox[0]?.id;
    const detection = detectionsRef.current?.find((d) => d.sampleId === id);
    if (detection) {
      setSelected(detection);
    }
  }, []);

  const isSelected = (sampleId: string) => selected?.sampleId === sampleId;

  const coloredBboxList = detections
    ?.map((detection) => (detection.sampleId
      ? ({
        ...detection.rect,
        id: detection.sampleId,
        isSelected: isSelected(detection.sampleId),
        style: bboxStyles ?? DEFAULT_BBOX_STYLES,
        key: detection.sampleId,
      })
      : undefined))
    ?.filter((b) => b !== undefined);

  const onChangeFileHandler = async (newFile: TFileDropzoneFile | undefined) => {
    setFile(newFile);
    if (!newFile?.raw) return;
    setError(undefined);
    resetDetections();

    const sdkResult = await estimateSdk.mutateAsync({
      data: newFile.raw,
      params: {
        detect_face: 1 as never,
        estimate_face_descriptor: 1 as never,
        estimate_face_warp: 1 as never,
        image_type: 0 as never,
      },
    });

    const imageEstimations = sdkResult.images_estimations?.[0];
    if (!imageEstimations || 'error' in imageEstimations) {
      setError(t('shared:faceDetector.errors.image_does_not_contain_faces'));
      return;
    }

    const faceEstimations = imageEstimations.estimations
      ?.filter((e) => {
        const warp = e.face?.detection?.warp;
        const descriptor = e.face?.detection?.attributes?.descriptor?.sdk_descriptor;
        const rect = e.face?.detection?.rect;
        return warp != null && descriptor != null && rect != null;
      })
      ?? [];

    if (faceEstimations.length === 0) {
      setError(t('shared:faceDetector.errors.image_does_not_contain_faces'));
      return;
    }

    const processedDetections: FaceDetectorDetection[] = await faceEstimations
      .reduce(async (accPromise, estimation) => {
        const acc = await accPromise;
        const warp = estimation.face?.detection?.warp;
        const descriptor = estimation.face?.detection?.attributes?.descriptor?.sdk_descriptor;
        const rect = estimation.face?.detection?.rect;

        if (!warp || !descriptor || !rect) return acc;

        const warpBlob = base64ToBlob(warp);
        const savedSample = await saveSampleMutation.mutateAsync({
          sampleType: 'faces',
          data: warpBlob,
          params: { ttl: 1 },
        });

        acc.push({
          sampleId: savedSample.sample_id,
          sdkDescriptor: descriptor,
          rect,
        });
        return acc;
      }, Promise.resolve([] as FaceDetectorDetection[]));

    setDetections(processedDetections);
    if (processedDetections.length === 1) {
      setSelected(processedDetections[0]);
    }
  };

  return (
    <div className={classNames(st.Root, className)}>
      <FileDropzone
        accept={accept}
        className={classNames(dropzoneClassName)}
        data-testid="fileDropzone"
        disabledWhenDetections={disabledWhenDetections}
        error={error}
        fileViewer={{
          [file?.raw?.type as string]: (<FileWithDetectionsViewer
            coloredBboxList={coloredBboxList}
            file={file}
            onBBoxClick={onBBoxClick}
          />),
        }}
        localFile={file?.raw}
        maxImageSize={maxImageSize}
        onChangeValue={onChangeFileHandler}
        onlyImages
        onReset={reset}
      />
      {!!estimateSdk.isPending && <span className={st.Description}>{t('shared:faceDetector.detection_is_pending')}</span>}
      {!!estimateSdk.isSuccess && !!detections && (
        <span className={st.Description}>
          {t('shared:faceDetector.detection_count', { count: detections.length })}
        </span>
      )}
    </div>
  );
}
